import React, { useState } from 'react';
import { Typography, Checkbox, Button, message, Divider } from 'antd';
import { useAtom } from 'jotai';
import dayjs from 'dayjs';
import classNames from 'classnames';
import Card from 'components/Card/Card';
import Html from 'Html';
import { checklistStatusAtom, programDetailsAtom, programDetailsExpandedAtom } from '../../../atoms';
import familyService from 'services/family.service';
import legacyService from 'services/legacy.service';
import { DATE_TIME_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';

const { Text } = Typography;

export default function ProgramDetailsCard({ applicationId }) {
  const [programDetails, setProgramDetails] = useAtom(programDetailsAtom);
  const [isExpanded, setIsExpanded] = useAtom(programDetailsExpandedAtom);
  const [, setStatusData] = useAtom(checklistStatusAtom);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!programDetails) return null;

  const { accepted, acceptedDate, name, content } = programDetails;

  const refreshAfterAccept = async () => {
    const [fresh, status] = await Promise.all([
      familyService.getProgramDetails(applicationId),
      legacyService.getChecklistStatus(applicationId).catch(() => null),
    ]);
    setProgramDetails(fresh);
    if (status) setStatusData(status);
    setIsExpanded(false);
  };

  const handleAccept = async () => {
    if (!checked) return;
    try {
      setSubmitting(true);
      await familyService.acceptProgramDetails(applicationId);
      await refreshAfterAccept();
      message.success('Acknowledgement recorded');
    } catch (error) {
      if (error?.response?.status === 409) {
        try {
          await refreshAfterAccept();
        } catch {}
      } else {
        message.error('Failed to confirm. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (accepted) {
    return (
      <div id="program-details-card" className="mb-4">
        <Card
          collapsible
          defaultOpen={false}
          size="small"
          title={name}
          subtitle={
            <Text className="text-gray-500 text-sm">
              Acknowledged {dayjs(acceptedDate).format(DATE_TIME_FORMAT_SENTENCE)}
            </Text>
          }
          extra={
            <span className="text-sm font-semibold text-green-700 bg-green-100 rounded-full px-4 py-1.5 whitespace-nowrap">
              Acknowledged
            </span>
          }
          noBodyPadding
        >
          <div className="p-6 pb-0">
            <Html html={content} />
          </div>

          <Divider className="my-4" />

          <div className="px-6 pb-6">
            <Text className="text-gray-400 text-sm">
              You acknowledged this on {dayjs(acceptedDate).format(DATE_TIME_FORMAT_SENTENCE)}
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div id="program-details-card" className="mb-4">
      <Card
        collapsible
        open={isExpanded}
        onOpenChange={setIsExpanded}
        size="small"
        title={name}
        subtitle={
          <Text className="text-sm" type="secondary">
            Please review and acknowledge
          </Text>
        }
        extra={
          <span className="text-sm font-semibold bg-[#e6f0ff] text-[#4654fc] rounded-full px-4 py-1.5 whitespace-nowrap">
            Start here · 30 seconds
          </span>
        }
        className={classNames({
          'ring-4 ring-[#e6f0ff] border-solid border-2 !border-[#5d82e4] rounded-2xl': isExpanded,
        })}
        styles={{
          extra: {
            pointerEvents: 'none',
          },
        }}
        noBodyPadding
      >
        <div className="p-6 pb-0" onClick={(e) => e.stopPropagation()}>
          <Html html={content} />
        </div>

        <Divider className="mb-4" />

        <div className="p-6 pt-0" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
            <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)}>
              I've reviewed and acknowledge these program details
            </Checkbox>
            <Button type="primary" disabled={!checked} loading={submitting} onClick={handleAccept}>
              Confirm acknowledgement
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
